import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CasinoIcon from "@mui/icons-material/Casino";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GridViewIcon from "@mui/icons-material/GridView";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import LightModeIcon from "@mui/icons-material/LightMode";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LocalCafeIcon from "@mui/icons-material/LocalCafe";
import LockResetIcon from "@mui/icons-material/LockReset";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import SensorDoorIcon from "@mui/icons-material/SensorDoor";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import { Box, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { SvgIconComponent } from "@mui/icons-material";

export const LOGIN_NOTE_ICON_KEYS = [
  "view_in_ar",
  "favorite",
  "local_cafe",
  "casino",
  "thumb_up",
  "sensor_door",
  "rocket_launch",
  "light_mode",
  "celebration",
  "emoji_events",
  "grid_view",
  "hourglass_top",
] as const;

export const AUTH_FLOW_ICON_KEYS = [
  "verified_user",
  "sms",
  "mark_email_unread",
  "mark_email_read",
  "lock_reset",
  "check_circle",
  "link_off",
  "warning_amber",
] as const;

export const AUTH_HEADER_ICON_KEYS = [
  ...LOGIN_NOTE_ICON_KEYS,
  ...AUTH_FLOW_ICON_KEYS,
] as const;

export type LoginNoteIconKey = (typeof LOGIN_NOTE_ICON_KEYS)[number];
export type AuthHeaderIconKey = (typeof AUTH_HEADER_ICON_KEYS)[number];

const authHeaderIconMap: Record<AuthHeaderIconKey, SvgIconComponent> = {
  view_in_ar: ViewInArIcon,
  favorite: FavoriteIcon,
  local_cafe: LocalCafeIcon,
  casino: CasinoIcon,
  thumb_up: ThumbUpIcon,
  sensor_door: SensorDoorIcon,
  rocket_launch: RocketLaunchIcon,
  light_mode: LightModeIcon,
  celebration: CelebrationIcon,
  emoji_events: EmojiEventsIcon,
  grid_view: GridViewIcon,
  hourglass_top: HourglassTopIcon,
  verified_user: VerifiedUserOutlinedIcon,
  sms: SmsOutlinedIcon,
  mark_email_unread: MarkEmailUnreadOutlinedIcon,
  mark_email_read: MarkEmailReadOutlinedIcon,
  lock_reset: LockResetIcon,
  check_circle: CheckCircleOutlineIcon,
  link_off: LinkOffIcon,
  warning_amber: WarningAmberOutlinedIcon,
};

export const isLoginNoteIconKey = (value: string): value is LoginNoteIconKey =>
  LOGIN_NOTE_ICON_KEYS.includes(value as LoginNoteIconKey);

export const isAuthHeaderIconKey = (
  value: string,
): value is AuthHeaderIconKey =>
  AUTH_HEADER_ICON_KEYS.includes(value as AuthHeaderIconKey);

export const resolveAuthHeaderIcon = (
  iconKey: string | undefined,
): SvgIconComponent => {
  if (iconKey && isAuthHeaderIconKey(iconKey)) {
    return authHeaderIconMap[iconKey];
  }

  return AutoAwesomeIcon;
};

type AuthHeaderIconProps = {
  iconKey: AuthHeaderIconKey;
  testId?: string;
};

export function AuthHeaderIcon({
  iconKey,
  testId = "login-form-header-icon",
}: AuthHeaderIconProps) {
  const theme = useTheme();
  const Icon = resolveAuthHeaderIcon(iconKey);
  const accentColor = theme.palette.custom.orange;

  return (
    <Box
      component="span"
      data-testid={testId}
      aria-hidden
      sx={{
        display: "inline-flex",
        alignItems: "center",
        flexShrink: 0,
        lineHeight: 0,
        pointerEvents: "none",
        userSelect: "none",
        "@keyframes authHeaderIconPop": {
          "0%": {
            opacity: 0,
            transform: "translateY(5px)",
          },
          "100%": {
            opacity: 1,
            transform: "translateY(0)",
          },
        },
        animation: "authHeaderIconPop 0.4s ease-out",
        "@media (prefers-reduced-motion: reduce)": {
          animation: "none",
        },
      }}
    >
      <Icon
        sx={{
          fontSize: "1.2em",
          color: alpha(accentColor, 0.9),
        }}
      />
    </Box>
  );
}
